import { TourAnchor } from "training/anchors";
import {
  hasAnalytics,
  not,
  paymentNotesEnabled,
} from "training/engine/conditions";
import { QUIZ_PATH } from "training/paths";
import { ITourStep } from "training/types";

import { MODULE_4_DIRECT_PAY } from "./module4";
import { ESTIMATE_ACTIONS_MENU } from "./shared";

/** A reload inside the Reports tab restarts at the tab click: the engine opens the hospital view on Estimates. */
const REPORTS_TAB_RESUME = "m6.04-reports-tab";

/**
 * CLE-3015 Module 6: Tracking an estimate, notes and reports, then wrap-up. Text is the curriculum's
 * (curriculum-v3, 2026-10-21), verbatim, except where a comment says otherwise.
 *
 * Reorder: 4.10 (Direct Pay, Module 4) runs right before 6.4, on the same hospital view (see module4.ts).
 *
 * Since the Estimate Details redesign (CLE-2961): "Estimate history" is the "Activity Timeline" card, it
 * lists the oldest entry first (OrderView sorts it ascending), and "Add Note" is an icon button labelled
 * "Add note". 6.1's "newest first" is corrected to "oldest first"; the rest is kept.
 *
 * 6.3 is the tour's one write after Create and Send: a plain note, which changes nothing about the money.
 *
 * Wrap-up: 6.7 offers Cancel (a look — the trainee cancels after the tour if their estimate was a test).
 * 6.8 (the knowledge check) and 6.9 (the certificate) are PR 4: the tour ends on a card that says so.
 */
export const MODULE_6: ITourStep[] = [
  {
    id: "m6.01-history",
    module: 6,
    anchor: TourAnchor.ActivityTimeline,
    label: "Activity Timeline",
    mode: "look",
    route: "estimate",
    // "newest first" in the curriculum; the card lists the oldest first.
    text: 'Everything that happened, oldest first: created and by whom, each text and email sent, "Estimate Opened" when the patient looked at it, payments with who collected them.',
  },
  {
    // Shown when the history has more than three entries.
    id: "m6.01b-show-more",
    module: 6,
    anchor: TourAnchor.ShowMore,
    label: "Show More",
    mode: "click",
    route: "estimate",
    optional: true,
    waitMs: 1000,
    text: 'Click "Show More" to see it all.',
  },
  {
    // On the first sent text or email whose wording we kept (CLE-2576); the trainee may open it.
    id: "m6.02-view-message",
    module: 6,
    anchor: TourAnchor.ViewMessage,
    label: "View message",
    mode: "fill",
    route: "estimate",
    optional: true,
    waitMs: 1500,
    text: "The exact words the patient received. Useful when they call about it.",
  },
  {
    id: "m6.03-add-note",
    module: 6,
    anchor: TourAnchor.AddNote,
    mode: "click",
    route: "estimate",
    text: "Write down every conversation here: the patient called, will pay on the day, wants a callback. The note goes in the history with your name and time, and Clear Health support sees it too. A plain note is safe to save.",
  },
  {
    // Instruction column. The payment-note box shows only where the hospital allows payment notes.
    id: "m6.03b-note-text",
    module: 6,
    anchor: TourAnchor.AddNoteText,
    mode: "fill",
    route: "estimate",
    when: paymentNotesEnabled,
    waitMs: 3000,
    text: 'Type "Clear Certified training", and leave "This is a payment note" unticked.',
  },
  {
    // Instruction column, without the payment-note words (the 6.3 branch).
    id: "m6.03n-note-text",
    module: 6,
    anchor: TourAnchor.AddNoteText,
    mode: "fill",
    route: "estimate",
    when: not(paymentNotesEnabled),
    waitMs: 3000,
    text: 'Type "Clear Certified training".',
  },
  {
    // Instruction column. Add stays disabled until the note has text.
    id: "m6.03c-add",
    module: 6,
    anchor: TourAnchor.AddNoteSubmit,
    label: "Add",
    mode: "click",
    route: "estimate",
    text: 'Click "Add".',
  },
  // Counted in Module 6, where it runs: as Module 4's last step its counter read "Module 4 · step 20 of
  // 20" in the middle of Module 6 (dev QA of #1438). It stays a Module 4 step (id, text, telemetry).
  { ...MODULE_4_DIRECT_PAY, countIn: 6 },
  {
    // 6.4 is two highlights (the tab, then the list inside it), split at a sentence boundary.
    id: "m6.04-reports-tab",
    // First on the hospital view when Direct Pay is off.
    waitMs: 8000,
    module: 6,
    anchor: TourAnchor.ReportsTab,
    label: "Reports",
    mode: "click",
    route: "estimates",
    text: "Every Tuesday morning Clear Health publishes last week's payments here, one report per payout. Your business office uses it to post payments.",
  },
  {
    id: "m6.04b-reports-table",
    // Inside the tab the click just opened; the list loads first.
    waitMs: 8000,
    module: 6,
    anchor: TourAnchor.ReportsTable,
    label: "Report Date",
    mode: "look",
    route: "estimates",
    resumeAt: REPORTS_TAB_RESUME,
    text: "If the list is empty, your hospital has no reports yet, or ask support for access.",
  },
  {
    id: "m6.05-paid-estimates-download",
    waitMs: 3000,
    module: 6,
    anchor: TourAnchor.PaidEstimatesDownload,
    label: "Estimate Paid Date",
    mode: "look",
    route: "estimates",
    resumeAt: REPORTS_TAB_RESUME,
    when: hasAnalytics,
    text: "Need who paid before Tuesday? Pick a date range here and download the list of paid estimates.",
  },
  {
    // 6.6 points at two tabs, split at a sentence boundary.
    id: "m6.06-providers-tab",
    module: 6,
    anchor: TourAnchor.ProvidersTab,
    label: "Providers",
    mode: "look",
    route: "estimates",
    text: "Providers is the list of facilities and physicians on your estimates.",
  },
  {
    id: "m6.06b-contracts-tab",
    module: 6,
    anchor: TourAnchor.ContractsTab,
    label: "Contracts",
    mode: "look",
    route: "estimates",
    text: "Contracts is where your payer contracts live. Most front-desk users never need either.",
  },
  {
    id: "m6.10-support-notes-card",
    module: 6,
    mode: "card",
    route: "estimates",
    when: paymentNotesEnabled,
    text: 'Clear Health support calls patients who have not paid and leaves a note in the same history, so check it before you call someone yourself. If a patient paid part in cash or by check at the desk, use Add Note and tick "This is a payment note" with the amount; where your hospital has this on, it lowers what the patient owes.',
  },
  {
    // The 6.3 branch: without payment notes, the card drops its cash-note sentence.
    id: "m6.10n-support-notes-card",
    module: 6,
    mode: "card",
    route: "estimates",
    when: not(paymentNotesEnabled),
    text: "Clear Health support calls patients who have not paid and leaves a note in the same history, so check it before you call someone yourself.",
  },
  {
    id: "m6.11-chat-card",
    module: 6,
    mode: "card",
    route: "estimates",
    text: "Questions a patient asks our chat assistant are kept by Clear Health. If you need them, ask support.",
  },
  {
    id: "m6.12-support-card",
    module: 6,
    mode: "card",
    route: "estimates",
    text: "Support: 888-918-2522 and support@clearhealthinc.com, any time you are unsure.",
  },
  {
    id: "m6.07-cancel-estimate",
    module: 6,
    anchor: TourAnchor.CancelEstimate,
    label: "Cancel Estimate",
    mode: "look",
    route: "estimate",
    opener: ESTIMATE_ACTIONS_MENU,
    text: 'If this estimate was a test with made-up details, cancel it now so it stops sending reminders and does not count in your monthly numbers. In the note, type "training". If you made it for a real patient, skip this step.',
  },
  {
    // Not curriculum text — the end of the walk-through, and the hand-off to 6.8 (the knowledge check,
    // /training/quiz) and 6.9 (the certificate), which are pages rather than tour steps (PR 4).
    id: "m6.07b-walkthrough-done",
    module: 6,
    mode: "card",
    route: "estimate",
    continueLabel: "Start the knowledge check",
    onFinishNavigateTo: QUIZ_PATH,
    text: 'That is the end of the walk-through. To cancel a test estimate, choose "Cancel Estimate" in the "..." menu; you can come back to it after the knowledge check. Next: twelve quick questions. Get one wrong and we show you the answer and where it lives.',
  },
];
